import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
  Grid,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getStatusLabel, getStatusColor } from '../../utils/availability';
import { getProductUrl } from '../../config';

const MobileCampView = ({ camps, locations, loading, error, fetchCamps }) => {
  const [expandedCamp, setExpandedCamp] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(locations?.[0] || null);

  // Set default location when locations load
  React.useEffect(() => {
    if (locations.length > 0 && !selectedLocation) {
      setSelectedLocation(locations[0]);
    }
  }, [locations, selectedLocation]);

  const toggleCampExpand = (campHandle) => {
    setExpandedCamp(expandedCamp === campHandle ? null : campHandle);
  };

  if (loading) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Loading camps...</Typography>
      <CircularProgress />
    </Container>
  );

  if (error) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" color="error" sx={{ mb: 2 }}>{error}</Typography>
      <Button variant="contained" onClick={() => fetchCamps()} sx={{ mt: 2 }}>Retry</Button>
    </Container>
  );

  if (!camps.length) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>No camps available at this time</Typography>
      <Button variant="contained" onClick={() => fetchCamps()} sx={{ mt: 2 }}>Refresh</Button>
    </Container>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Typography variant="h4" component="h1" align="center" gutterBottom>
        Summer Camps 2026
      </Typography>

      {/* Location filter */}
      {locations.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={selectedLocation}
            exclusive
            onChange={(e, val) => { if (val !== null) setSelectedLocation(val); }}
            size="small"
          >
            {locations.map(loc => (
              <ToggleButton key={loc} value={loc} sx={{ px: 2, fontSize: '0.8rem' }}>
                {loc}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {camps.map((camp) => {
        // Get slots for selected location
        const locationSlots = camp.weekSlots.filter(s =>
          selectedLocation ? s.location === selectedLocation : true
        );
        const availableCount = locationSlots.filter(s => s.status !== 'sold_out').length;
        const lowCount = locationSlots.filter(s => s.status === 'low').length;

        return (
          <Card
            key={camp.handle}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: expandedCamp === camp.handle ? 3 : 1,
              transition: 'all 0.3s ease',
              border: expandedCamp === camp.handle ? '2px solid #2196f3' : 'none'
            }}
          >
            <CardContent
              sx={{ p: 2, '&:last-child': { pb: 2 }, cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
              onClick={() => toggleCampExpand(camp.handle)}
            >
              <Box sx={{ display: 'flex', mb: 1 }}>
                {camp.imageUrl && (
                  <Box
                    component="img"
                    src={camp.imageUrl}
                    alt={camp.imageAlt}
                    sx={{ width: 80, height: 80, borderRadius: 1, mr: 2, objectFit: 'cover' }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                    {camp.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Ages: 10-12 • {camp.tags.includes('beginner') ? 'Beg-Int' : 'Beg-Adv'}
                  </Typography>
                  {/* Quick availability summary */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {availableCount > 0 && (
                      <Chip
                        label={`${availableCount} dates open`}
                        size="small"
                        sx={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    {lowCount > 0 && (
                      <Chip
                        label={`${lowCount} almost full`}
                        size="small"
                        sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    {availableCount === 0 && (
                      <Chip
                        label="All dates sold out"
                        size="small"
                        sx={{ backgroundColor: '#f44336', color: 'white', fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(getProductUrl(camp.handle), '_blank');
                        }}
                        sx={{ borderRadius: 4, textTransform: 'none', fontSize: '0.8rem', py: 0.5, width: '100%' }}
                      >
                        Course info
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCampExpand(camp.handle);
                        }}
                        sx={{
                          borderRadius: 4, textTransform: 'none', fontSize: '0.8rem', py: 0.5,
                          bgcolor: '#8BC34A', '&:hover': { bgcolor: '#7CB342' }, width: '100%'
                        }}
                      >
                        Select Dates
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </CardContent>

            {/* Expanded view with per-week availability */}
            <Collapse in={expandedCamp === camp.handle}>
              <Divider />
              <CardContent sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Available Dates</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setExpandedCamp(null)}
                    sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {locationSlots.map((slot) => {
                  const statusColor = getStatusColor(slot.status);
                  const statusLabel = getStatusLabel(slot.status, slot.quantityAvailable);
                  const isSoldOut = slot.status === 'sold_out';

                  return (
                    <Card
                      key={slot.variantId}
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        borderColor: slot.status === 'low' ? '#ff9800' : slot.status === 'sold_out' ? 'rgba(244,67,54,0.3)' : '#e0e0e0',
                        borderRadius: 2,
                        opacity: isSoldOut ? 0.6 : 1
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 'medium',
                                textDecoration: isSoldOut ? 'line-through' : 'none'
                              }}
                            >
                              {slot.dates}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statusColor, mr: 1 }} />
                              <Chip
                                label={statusLabel}
                                size="small"
                                sx={{
                                  backgroundColor: statusColor,
                                  color: 'white',
                                  fontWeight: 'medium',
                                  fontSize: '0.65rem',
                                  height: 20
                                }}
                              />
                            </Box>
                          </Box>
                          {!isSoldOut && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={{
                                borderRadius: 4, px: 2, py: 0.5, textTransform: 'none',
                                bgcolor: '#8BC34A', '&:hover': { bgcolor: '#7CB342' }
                              }}
                              onClick={() => window.open(getProductUrl(camp.handle), '_blank')}
                            >
                              Select
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                {locationSlots.length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                      No dates available for this location
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Collapse>
          </Card>
        );
      })}
    </Container>
  );
};

export default MobileCampView;
